import React, { useState } from 'react';
import { useAdmin } from '../../hooks/useAdmin';
import LoadingState from '../shared/LoadingState';
import { Edit, Trash2, X, Plus, Search, Calendar } from 'lucide-react';

export default function ConvenerManagement() {
  const { users, meetingTypes, addConvener, updateConvener, deleteConvener, loading: dataLoading } = useAdmin();
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingConvener, setEditingConvener] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleAddConvener = async (formData: any) => {
    setLoading(true);
    setError('');

    const result = await addConvener(formData);
    
    if (result.data) {
      setShowAddModal(false);
    } else {
      setError(result.error?.message || 'Failed to add convener');
    }
    
    setLoading(false);
  };

  const handleEditConvener = async (userId: string, formData: any) => {
    setLoading(true);
    setError('');

    const result = await updateConvener(userId, formData);
    
    if (result.data) {
      setEditingConvener(null);
    } else {
      setError(result.error?.message || 'Failed to update convener');
    }
    
    setLoading(false);
  };

  const handleDeleteConvener = async (userId: string) => {
    if (!window.confirm('Are you sure you want to delete this convener? This will remove all their meeting assignments.')) return;

    setLoading(true);
    const result = await deleteConvener(userId);
    
    if (result.error) {
      setError(result.error?.message || 'Failed to delete convener');
    }
    
    setLoading(false);
  };

  const openEditModal = (user: any) => {
    const assignedIds = user.user_meeting_assignments?.map(
      (uma: any) => uma.meeting_type_id || uma.meeting_types?.id
    ).filter(Boolean) || [];

    setEditingConvener({
      ...user,
      meeting_type_ids: assignedIds
    });
  };

  const closeModals = () => {
    setShowAddModal(false);
    setEditingConvener(null);
    setError('');
  };

  const conveners = users.filter(user => !user.is_admin);
  const filteredConveners = conveners.filter(user => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    const nameMatch = user.name?.toLowerCase().includes(q);
    const phoneMatch = user.phone?.includes(q);
    const roleMatch = user.role?.toLowerCase().includes(q);
    const assignmentMatch = user.user_meeting_assignments?.some((uma: any) =>
      uma.meeting_types?.name?.toLowerCase().includes(q)
    );
    return nameMatch || phoneMatch || roleMatch || assignmentMatch;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <h2 className="text-lg font-semibold text-gray-900">
            Manage Conveners ({dataLoading ? '...' : filteredConveners.length}{!dataLoading && conveners.length !== filteredConveners.length ? ` of ${conveners.length}` : ''})
          </h2>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition w-full sm:w-auto text-sm font-medium"
          >
            <Plus size={16} />
            <span>Add Convener</span>
          </button>
        </div>

        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search conveners by name, phone, role, or assigned meeting types..."
            className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X size={16} />
            </button>
          )}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        {/* Conveners Content */}
        {dataLoading ? (
          <LoadingState title="Loading Conveners..." minHeight="min-h-[300px]" />
        ) : filteredConveners.length > 0 ? (
          <div>
            {/* Desktop Table View */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Meeting Types</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Meeting Activity</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredConveners.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                            <span className="text-blue-600 font-medium">
                              {user.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{user.name}</div>
                            <div className="text-xs text-gray-500">Convener</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                        {user.phone}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                          {user.role}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {user.user_meeting_assignments?.length > 0 
                          ? user.user_meeting_assignments.map((uma: any) => 
                              uma.meeting_types?.name
                            ).join(', ')
                          : 'No assignments'
                        }
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {user.last_meeting_date ? (
                          <div className="flex items-center space-x-1.5 text-gray-900 font-medium">
                            <Calendar size={14} className="text-blue-600" />
                            <span>{user.last_meeting_date}</span>
                          </div>
                        ) : (
                          <span className="text-gray-400 italic text-xs">No meetings created</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => openEditModal(user)}
                            className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={() => handleDeleteConvener(user.id)}
                            disabled={loading}
                            className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition disabled:opacity-50"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="sm:hidden space-y-3">
              {filteredConveners.map((user) => (
                <div key={user.id} className="border border-gray-200 rounded-lg p-4 bg-white hover:bg-gray-50 transition space-y-2">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-blue-600 font-semibold text-base">
                          {user.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-gray-900">{user.name}</div>
                        <div className="text-xs text-gray-500 font-mono">{user.phone}</div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={() => openEditModal(user)}
                        className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition"
                        title="Edit"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={() => handleDeleteConvener(user.id)}
                        disabled={loading}
                        className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded transition disabled:opacity-50"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-gray-100 text-xs">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="px-2 py-0.5 font-medium rounded-full bg-blue-100 text-blue-800">
                        {user.role}
                      </span>
                      <span className="text-gray-500">
                        {user.user_meeting_assignments?.length > 0
                          ? user.user_meeting_assignments.map((uma: any) => uma.meeting_types?.name).join(', ')
                          : 'No assignments'}
                      </span>
                    </div>

                    <div className="text-xs font-semibold text-gray-700 flex items-center space-x-1">
                      <Calendar size={13} className="text-blue-600" />
                      <span>{user.last_meeting_date ? user.last_meeting_date : 'No meetings'}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500 text-sm">
            {searchQuery ? 'No conveners match your search criteria.' : 'No conveners found. Add your first convener to get started!'}
          </div>
        )}
      </div>

      {/* Add Convener Modal */}
      {showAddModal && (
        <ConvenerModal
          title="Add New Convener"
          meetingTypes={meetingTypes}
          onSubmit={handleAddConvener}
          onClose={closeModals}
          loading={loading}
        />
      )}

      {/* Edit Convener Modal */}
      {editingConvener && (
        <ConvenerModal
          title="Edit Convener"
          meetingTypes={meetingTypes}
          initialData={editingConvener}
          onSubmit={(data) => handleEditConvener(editingConvener.id, data)}
          onClose={closeModals}
          loading={loading}
        />
      )}
    </div>
  );
}

// Convener Modal Component
interface ConvenerModalProps {
  title: string;
  meetingTypes: any[];
  initialData?: any;
  onSubmit: (data: any) => void;
  onClose: () => void;
  loading: boolean;
}

function ConvenerModal({ title, meetingTypes, initialData, onSubmit, onClose, loading }: ConvenerModalProps) {
  const [formData, setFormData] = useState({
    phone: initialData?.phone || '',
    name: initialData?.name || '',
    role: initialData?.role || 'Convener',
    is_admin: initialData?.is_admin || false,
    meeting_type_ids: initialData?.meeting_type_ids || []
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleMeetingTypeToggle = (meetingTypeId: string) => {
    setFormData(prev => ({
      ...prev,
      meeting_type_ids: prev.meeting_type_ids.includes(meetingTypeId)
        ? prev.meeting_type_ids.filter((id: string) => id !== meetingTypeId)
        : [...prev.meeting_type_ids, meetingTypeId]
    }));
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-end sm:items-center justify-center z-50 p-0 sm:p-4 transition-opacity">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-2xl max-h-[92vh] sm:max-h-[90vh] overflow-hidden flex flex-col shadow-2xl animate-in fade-in slide-in-from-bottom-4 duration-200">
        {/* Mobile Pull Indicator */}
        <div className="w-12 h-1.5 bg-gray-300 rounded-full mx-auto my-2 sm:hidden shrink-0" />

        <div className="flex items-center justify-between px-4 sm:px-6 py-3.5 border-b border-gray-200 shrink-0">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900">{title}</h2>
          <button
            onClick={onClose}
            disabled={loading}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition disabled:opacity-50 min-w-[40px] min-h-[40px] flex items-center justify-center"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4 overflow-y-auto flex-1">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5">
                Name
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-xs sm:text-sm bg-white"
                required
                disabled={loading}
              />
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5">
                Phone Number
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-xs sm:text-sm bg-white"
                required
                disabled={loading}
              />
            </div>
          </div>

          <div>
            <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5">
              Role
            </label>
            <input
              type="text"
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              placeholder="e.g. Finance Convener, Media Convener"
              className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-xs sm:text-sm bg-white"
              required
              disabled={loading}
            />
          </div>

          <div className="flex items-center pt-1">
            <input
              type="checkbox"
              id="is_admin"
              checked={formData.is_admin}
              onChange={(e) => setFormData({ ...formData, is_admin: e.target.checked })}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              disabled={loading}
            />
            <label htmlFor="is_admin" className="ml-2 text-xs sm:text-sm font-medium text-gray-700">
              Make this user an admin
            </label>
          </div>

          <div>
            <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5">
              Assign Meeting Types
            </label>
            <div className="border border-gray-200 rounded-xl p-3 max-h-48 overflow-y-auto space-y-1 bg-gray-50/50">
              {meetingTypes.map((type) => (
                <label key={type.id} className="flex items-center space-x-3 p-2 hover:bg-white rounded-lg cursor-pointer transition">
                  <input
                    type="checkbox"
                    checked={formData.meeting_type_ids.includes(type.id)}
                    onChange={() => handleMeetingTypeToggle(type.id)}
                    disabled={loading}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <div>
                    <div className="font-semibold text-gray-900 text-xs sm:text-sm">{type.name}</div>
                    <div className="text-[11px] text-gray-500">{type.level}</div>
                  </div>
                </label>
              ))}
            </div>
            <p className="text-[11px] text-gray-500 mt-1">
              Select meeting types this convener can manage
            </p>
          </div>

          <div className="flex space-x-3 pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="flex-1 px-4 py-2.5 text-xs sm:text-sm font-semibold border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-100 transition disabled:opacity-50 min-h-[44px]"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2.5 bg-blue-600 text-white text-xs sm:text-sm font-semibold rounded-xl hover:bg-blue-700 transition disabled:opacity-50 min-h-[44px] shadow-sm flex items-center justify-center space-x-2"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                <span>{(initialData ? 'Update' : 'Add') + ' Convener'}</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
